<template>
  <div class="import-wizard">
    <div class="stepper" role="navigation" aria-label="Import progress">
      <div class="stepper-header" role="list">
        <div
          :class="['step', currentStep >= 1 ? 'active' : '', currentStep > 1 ? 'completed' : '']"
          role="listitem"
          :aria-current="currentStep === 1 ? 'step' : null"
        >
          <div class="step-number" aria-hidden="true">1</div>
          <div class="step-label">Upload CSV</div>
        </div>

        <div class="step-divider" aria-hidden="true"></div>

        <div
          :class="['step', currentStep >= 2 ? 'active' : '', currentStep > 2 ? 'completed' : '']"
          role="listitem"
          :aria-current="currentStep === 2 ? 'step' : null"
        >
          <div class="step-number" aria-hidden="true">2</div>
          <div class="step-label">Map Columns</div>
        </div>

        <div class="step-divider" aria-hidden="true"></div>

        <div
          :class="['step', currentStep >= 3 ? 'active' : '', currentStep > 3 ? 'completed' : '']"
          role="listitem"
          :aria-current="currentStep === 3 ? 'step' : null"
        >
          <div class="step-number" aria-hidden="true">3</div>
          <div class="step-label">Preview & Import</div>
        </div>

        <div class="step-divider" aria-hidden="true"></div>

        <div
          :class="['step', currentStep >= 4 ? 'active' : '']"
          role="listitem"
          :aria-current="currentStep === 4 ? 'step' : null"
        >
          <div class="step-number" aria-hidden="true">4</div>
          <div class="step-label">Results</div>
        </div>
      </div>
      
      <div class="stepper-content">
        <!-- Step 1: Upload CSV -->
        <div v-if="currentStep === 1" class="step-content">
          <file-uploader
            @file-selected="handleFileSelected"
            @uploaded="handleFileUploaded"
          />
          
          <div class="actions">
            <button 
              class="btn btn-primary" 
              :disabled="!fileUploaded" 
              @click="nextStep"
            >
              Continue
            </button>
          </div>
        </div>
        
        <!-- Step 2: Map Columns -->
        <div v-if="currentStep === 2" class="step-content">
          <column-mapper
            v-show="fileUploaded"
            :headers="csvHeaders"
            :preview-data="previewData"
            :available-fields="availableFields"
            ref="columnMapper"
          />
          
          <div class="actions">
            <button class="btn btn-secondary" @click="prevStep">Back</button>
            <button 
              class="btn btn-primary" 
              :disabled="!canProceed" 
              @click="nextStep"
            >
              Preview Import
            </button>
          </div>
        </div>
        
        <!-- Step 3: Preview & Import -->
        <div v-if="currentStep === 3" class="step-content">
          <import-preview
            ref="importPreview"
            :preview-data="mappedPreviewData"
            :options="importOptions"
            :session-types="sessionTypes"
            @update:options="updateImportOptions"
          />
          
          <div class="actions">
            <button class="btn btn-secondary" @click="prevStep">Back</button>
            <button 
              class="btn btn-primary" 
              :class="{ 'loading': importing }"
              @click="startImport"
            >
              <span v-if="importing">Importing...</span>
              <span v-else>Start Import</span>
            </button>
          </div>
        </div>
        
        <!-- Step 4: Results -->
        <div v-if="currentStep === 4" class="step-content">
          <import-results
            :results="importResults"
            :status="importStatus"
            :progress="importProgress"
            :failure-reason="importFailureReason"
          />
          
          <div class="actions">
            <button class="btn btn-secondary" @click="resetWizard">
              Import Another File
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FileUploader from './FileUploader.vue';
import ColumnMapper from './ColumnMapper.vue';
import ImportPreview from './ImportPreview.vue';
import ImportResults from './ImportResults.vue';
import { mapGetters } from 'vuex';

export default {
  components: {
    FileUploader,
    ColumnMapper,
    ImportPreview,
    ImportResults
  },
  
  data() {
    return {
      fileId: null,
      // Polling configuration
      pollingInterval: null,
      pollAttempts: 0,
      maxPollAttempts: 900, // 30 minutes at 2s intervals
      pollIntervalMs: 2000, // 2 seconds
    }
  },
  
  computed: {
    ...mapGetters([
      'schoolId',
      'studentImportCurrentStep',
      'studentImportFileUploaded',
      'studentImportFileInfo',
      'studentImportPreviewData',
      'studentImportOptions',
      'studentImportImporting',
      'studentImportResults',
      'studentImportMappedPreviewData',
      'studentImportSessionTypes',
      'studentImportId',
      'studentImportStatus',
      'studentImportProgress',
      'studentImportFailureReason'
    ]),
    
    currentStep: {
      get() {
        return this.studentImportCurrentStep;
      },
      set(value) {
        this.$store.dispatch('SET_STUDENT_IMPORT_STEP', value);
      }
    },
    
    fileUploaded: {
      get() {
        return this.studentImportFileUploaded;
      },
      set(value) {
        this.$store.dispatch('SET_STUDENT_IMPORT_FILE_UPLOADED', value);
      }
    },
    
    csvHeaders() {
      return this.studentImportPreviewData.headers;
    },
    
    previewData() {
      return this.studentImportPreviewData.data;
    },
    
    availableFields() {
      return this.studentImportPreviewData.fields;
    },
    
    importOptions: {
      get() {
        return this.studentImportOptions;
      },
      set(value) {
        this.$store.dispatch('SET_STUDENT_IMPORT_OPTIONS', value);
      }
    },
    
    importing() {
      return this.studentImportImporting;
    },
    
    importResults() {
      return this.studentImportResults;
    },
    
    canProceed() {
      if (this.currentStep === 1) {
        return this.fileUploaded;
      }

      if (this.currentStep === 2) {
        return this.$store.dispatch('VALIDATE_STUDENT_IMPORT_MAPPING');
      }

      return true;
    },
    
    mappedPreviewData() {
      return this.studentImportMappedPreviewData;
    },

    sessionTypes() {
      return this.studentImportSessionTypes;
    },

    importStatus() {
      return this.studentImportStatus;
    },

    importProgress() {
      return this.studentImportProgress;
    },

    importFailureReason() {
      return this.studentImportFailureReason;
    }
  },

  beforeDestroy() {
    // Clean up polling interval when component is destroyed
    this.stopPolling();
  },

  methods: {
    nextStep() {
      if (this.currentStep < 4) {
        const nextStep = this.currentStep + 1;

        // Fetch session types when entering step 3 (Preview)
        if (nextStep === 3 && this.sessionTypes.length === 0) {
          this.$store.dispatch('FETCH_STUDENT_IMPORT_SESSION_TYPES', { school_id: this.schoolId })
            .catch(error => {
              this.$store.dispatch('ui/ADD_FLASH_MESSAGE', {
                type: 'error',
                message: 'Failed to load session types: ' + (error.response?.data?.detail || error.response?.data?.error || 'Unknown error')
              });
            });
        }

        this.currentStep = nextStep;
      }
    },
    
    prevStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
      }
    },
    
    resetWizard() {
      this.$store.dispatch('RESET_STUDENT_IMPORT');
    },
    
    handleFileSelected(file) {
      // Handle file selection logic
    },
    
    handleFileUploaded(response) {
      this.fileUploaded = true;
      this.fileId = response.file.id;
      
      this.$store.dispatch('SET_STUDENT_IMPORT_FILE_INFO', {
        directory: response.file.directory,
        name: response.file.name
      });
      
      // Fetch preview data
      this.$store.dispatch('PREVIEW_STUDENT_IMPORT_FILE', {school_id: this.schoolId, file_id: response.file.id})
      .then(response => {
        this.$store.dispatch('SET_STUDENT_IMPORT_PREVIEW_DATA', {
          headers: response.data.headers,
          data: response.data.sample_data,
          fields: response.data.available_fields
        });
        
        // Set required fields
        const requiredFields = response.data.available_fields
          .filter(field => field.required)
          .map(field => field.value);
        this.$store.dispatch('SET_STUDENT_IMPORT_REQUIRED_FIELDS', requiredFields);
        
        this.nextStep();
      })
      .catch(error => {
        this.$store.dispatch('ui/ADD_FLASH_MESSAGE', {
          type: 'error',
          message: 'Failed to load preview: ' + (error.response?.data?.detail || error.response?.data?.error || 'Unknown error')
        });
      });
    },
    
    updateImportOptions(options) {
      this.importOptions = { ...options };
    },
    
    startImport() {
      // Validate session types are selected (only for non-test imports)
      if (!this.importOptions.test && !this.$refs.importPreview.validateSessionTypes()) {
        this.$store.dispatch('ui/ADD_FLASH_MESSAGE', {
          type: 'error',
          message: 'Please select both session types before starting the import.'
        });
        return;
      }

      this.$store.dispatch('SET_STUDENT_IMPORT_IMPORTING', true);

      this.$store.dispatch('START_STUDENT_IMPORT', {
        school_id: this.schoolId,
        file_id: this.fileId,
        test: this.importOptions.test,
        column_mapping: this.$store.state.studentImport.columnMapping,
        import_pickup_locations: this.importOptions.importPickupLocations,
        btw_session_type_id: this.importOptions.btwSessionTypeId,
        road_test_session_type_id: this.importOptions.roadTestSessionTypeId
      })
      .then(response => {
        // Check if this is an async response (202 with import_id)
        if (response.status === 202 && response.data.import_id) {
          // Async import - store the import_id and start polling
          this.$store.dispatch('SET_STUDENT_IMPORT_ID', response.data.import_id);
          this.$store.dispatch('SET_STUDENT_IMPORT_STATUS', 'pending');
          this.nextStep(); // Move to results step to show progress
          this.startPolling(response.data.import_id);
        } else {
          // Synchronous response (test mode) - results returned immediately
          this.$store.dispatch('SET_STUDENT_IMPORT_RESULTS', response.data);
          this.$store.dispatch('SET_STUDENT_IMPORT_STATUS', 'completed');
          this.$store.dispatch('SET_STUDENT_IMPORT_IMPORTING', false);
          this.nextStep();
        }
      })
      .catch(error => {
        this.$store.dispatch('SET_STUDENT_IMPORT_IMPORTING', false);
        this.$store.dispatch('ui/ADD_FLASH_MESSAGE', {
          type: 'error',
          message: 'Import failed: ' + (error.response?.data?.detail || error.response?.data?.error || 'Unknown error')
        });
      });
    },

    startPolling(importId) {
      this.pollAttempts = 0;
      this.pollingInterval = setInterval(() => {
        this.pollImportStatus(importId);
      }, this.pollIntervalMs);
    },

    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },

    pollImportStatus(importId) {
      this.pollAttempts++;

      // Safety check: stop polling after max attempts to prevent infinite loop
      if (this.pollAttempts >= this.maxPollAttempts) {
        this.stopPolling();
        this.$store.dispatch('SET_STUDENT_IMPORT_IMPORTING', false);
        this.$store.dispatch('SET_STUDENT_IMPORT_STATUS', 'failed');
        this.$store.dispatch('SET_STUDENT_IMPORT_FAILURE_REASON', 'Import timed out. Please check the import status manually or contact support.');
        this.$store.dispatch('ui/ADD_FLASH_MESSAGE', {
          type: 'error',
          message: 'Import polling timed out after 30 minutes.'
        });
        return;
      }

      this.$store.dispatch('GET_STUDENT_IMPORT_STATUS', {
        school_id: this.schoolId,
        import_id: importId
      })
      .then(response => {
        const status = response.data.status;

        if (status === 'completed' || status === 'failed') {
          this.stopPolling();
          this.$store.dispatch('SET_STUDENT_IMPORT_IMPORTING', false);

          if (status === 'completed') {
            this.$store.dispatch('ui/ADD_FLASH_MESSAGE', {
              type: 'success',
              message: 'Import completed successfully!'
            });
          } else {
            this.$store.dispatch('ui/ADD_FLASH_MESSAGE', {
              type: 'error',
              message: 'Import failed: ' + (response.data.failure_reason || 'Unknown error')
            });
          }
        }
      })
      .catch(error => {
        // On network error, don't immediately stop - allow some retries
        console.error('Polling error:', error);

        // Stop polling if we get a 404 (import not found) or other client error
        if (error.response && error.response.status >= 400 && error.response.status < 500) {
          this.stopPolling();
          this.$store.dispatch('SET_STUDENT_IMPORT_IMPORTING', false);
          this.$store.dispatch('SET_STUDENT_IMPORT_STATUS', 'failed');
          this.$store.dispatch('SET_STUDENT_IMPORT_FAILURE_REASON', 'Unable to retrieve import status.');
        }
      });
    }
  },
}
</script>

<style scoped lang="scss">
@import '../../../../assets/css/partials/variables';

.import-wizard {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell,
    'Open Sans', 'Helvetica Neue', sans-serif;
}

.stepper {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background-color: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.stepper-header {
  display: flex;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  color: #757575;
}

.step.active {
  color: $green;
}

.step.completed {
  color: $green;
}

.step-number {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #e0e0e0;
  color: #fff;
  margin-bottom: 8px;
}

.step.active .step-number {
  background-color: $green;
}

.step.completed .step-number {
  background-color: $green;
}

.step-divider {
  height: 1px;
  background-color: #e0e0e0;
  flex: 1;
  margin: 0 8px;
}

.stepper-content {
  padding: 24px;
}

.step-content {
  min-height: 400px;
  display: flex;
  flex-direction: column;
}

.actions {
  display: flex;
  justify-content: space-between;
  margin-top: 24px;
}

.btn {
  padding: 8px 16px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  text-transform: uppercase;
  transition: background-color 0.3s;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background-color: $green;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: $dark-green;
}

.btn-secondary {
  background-color: #f5f5f5;
  color: #757575;
}

.btn-secondary:hover {
  background-color: #e0e0e0;
}

.loading {
  position: relative;
  pointer-events: none;
}

.loading::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 1em;
  height: 1em;
  border-radius: 50%;
  border: 2px solid #fff;
  border-top-color: transparent;
  animation: spin 1s linear infinite;
  transform: translate(-50%, -50%);
}

@keyframes spin {
  to {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}
</style> 